import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  useApi,
  ApiError,
  type SessionDetailResponse,
  type SessionAssessmentRow,
  type SignoffListResponse,
  type SignoffRow,
} from '../api/client';
import { formatDateTime } from '../api/formatDate';
import { frameworkLabel, useFramework } from '../framework';
import { StateIcon } from './StateIcon';
import './ManagerSignoffs.css';

/**
 * Sign-off oversight queue at /manager/signoffs.
 *
 * Every saved assessment session auto-creates a 'pending' qa_signoff row
 * (see whanauAssessmentSessions.ts). Managers review here:
 *   - Approve → status='approved', stamps reviewed_at/by.
 *   - Return → status='returned' with a required review_notes paragraph.
 *
 * A tab strip lets the manager flip to approved/returned history for the
 * audit-trail view.
 *
 * Permission: team.signoff (manager+ default, server-enforced).
 */

function extractApiError(err: unknown, fallback: string): string {
   
  console.error('[signoffs]', err);
  if (err instanceof ApiError) {
    const body = err.body as { error?: unknown } | null;
    if (body && typeof body.error === 'string') return body.error;
    return err.message;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

type Tab = 'pending' | 'approved' | 'returned';

export function ManagerSignoffs() {
  const { t } = useTranslation();
  const callApi = useApi();

  const [tab, setTab] = useState<Tab>('pending');
  const [state, setState] = useState<
    | { kind: 'loading' }
    | { kind: 'ok'; data: SignoffListResponse }
    | { kind: 'error'; message: string }
  >({ kind: 'loading' });
  const [busy, setBusy] = useState<number | null>(null);
  const [returningId, setReturningId] = useState<number | null>(null);
  const [returnNotes, setReturnNotes] = useState('');
  /** Which signoff's session-detail modal is open. null = closed. */
  const [reviewing, setReviewing] = useState<SignoffRow | null>(null);

  async function load(currentTab: Tab) {
    setState({ kind: 'loading' });
    try {
      const data = await callApi<SignoffListResponse>(
        `/manager/signoffs?status=${currentTab}`,
      );
      setState({ kind: 'ok', data });
    } catch (err) {
      setState({ kind: 'error', message: extractApiError(err, 'Failed to load') });
    }
  }

  useEffect(() => {
    void load(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  async function approve(id: number) {
    if (!window.confirm(t('signoffs.confirmApprove'))) return;
    setBusy(id);
    try {
      await callApi(`/manager/signoffs/${id}/approve`, { method: 'POST' });
      await load(tab);
    } catch (err) {
      window.alert(extractApiError(err, 'Failed to approve'));
    } finally {
      setBusy(null);
    }
  }

  async function submitReturn(id: number, notesOverride?: string) {
    const notes = (notesOverride ?? returnNotes).trim();
    if (notes.length === 0) {
      window.alert(t('signoffs.returnNotesRequired'));
      return;
    }
    setBusy(id);
    try {
      await callApi(`/manager/signoffs/${id}/return`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ review_notes: notes }),
      });
      setReturningId(null);
      setReturnNotes('');
      await load(tab);
    } catch (err) {
      window.alert(extractApiError(err, 'Failed to return'));
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="ms">
      <header className="ms__header">
        <h1 className="ms__title">{t('signoffs.title')}</h1>
        <p className="ms__subtitle">{t('signoffs.subtitle')}</p>
      </header>

      <div className="ms__tabs" role="tablist">
        {(['pending', 'approved', 'returned'] as Tab[]).map((id) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            className={`ms__tab ${tab === id ? 'ms__tab--active' : ''}`}
            onClick={() => setTab(id)}
          >
            {t(`signoffs.tab.${id}`)}
          </button>
        ))}
      </div>

      {state.kind === 'loading' && (
        <p className="ms__status">{t('signoffs.loading')}</p>
      )}
      {state.kind === 'error' && (
        <p className="ms__error">{state.message}</p>
      )}
      {state.kind === 'ok' && state.data.rows.length === 0 && (
        <p className="ms__empty">{t('signoffs.empty')}</p>
      )}
      {state.kind === 'ok' && state.data.rows.length > 0 && (
        <ul className="ms__list">
          {state.data.rows.map((row) => (
            <SignoffCard
              key={row.signoff_id}
              row={row}
              busy={busy === row.signoff_id}
              isReturning={returningId === row.signoff_id}
              returnNotes={returnNotes}
              onSetReturning={(open) => {
                setReturningId(open ? row.signoff_id : null);
                setReturnNotes('');
              }}
              onReturnNotesChange={setReturnNotes}
              onApprove={() => void approve(row.signoff_id)}
              onSubmitReturn={() => void submitReturn(row.signoff_id)}
              onReview={() => setReviewing(row)}
            />
          ))}
        </ul>
      )}

      {reviewing && (
        <SessionReviewModal
          row={reviewing}
          busy={busy === reviewing.signoff_id}
          onClose={() => setReviewing(null)}
          onApprove={async () => {
            await approve(reviewing.signoff_id);
            setReviewing(null);
          }}
          onReturn={async (notes) => {
            await submitReturn(reviewing.signoff_id, notes);
            setReviewing(null);
          }}
        />
      )}
    </section>
  );
}

export default ManagerSignoffs;

// ============================================================================

function SignoffCard({
  row,
  busy,
  isReturning,
  returnNotes,
  onSetReturning,
  onReturnNotesChange,
  onApprove,
  onSubmitReturn,
  onReview,
}: {
  row: SignoffRow;
  busy: boolean;
  isReturning: boolean;
  returnNotes: string;
  onSetReturning: (open: boolean) => void;
  onReturnNotesChange: (notes: string) => void;
  onApprove: () => void;
  onSubmitReturn: () => void;
  onReview: () => void;
}) {
  const { t } = useTranslation();
  const isPending = row.status === 'pending';
  return (
    <li className={`ms-card ms-card--${row.status}`}>
      <div className="ms-card__main">
        <h2 className="ms-card__title">
          {isPending && row.assessment_session_id ? (
            <button
              type="button"
              className="ms-card__title-link"
              onClick={onReview}
            >
              {row.whanau_display_name ?? `#${String(row.whanau_id)}`}
            </button>
          ) : (
            <Link to={`/whanau/${String(row.whanau_id)}`}>
              {row.whanau_display_name ?? `#${String(row.whanau_id)}`}
            </Link>
          )}
        </h2>
        <p className="ms-card__meta">
          <span>
            {t('signoffs.requestedBy', {
              name:
                row.requested_by_display_name ??
                row.requested_by_oid.slice(0, 8) + '…',
            })}
          </span>
          {' · '}
          <span>{formatDateTime(row.requested_at)}</span>
          {row.cycle_label && (
            <>
              {' · '}
              <span className="ms-chip">{row.cycle_label}</span>
            </>
          )}
        </p>
        {row.review_notes && (
          <p className="ms-card__notes">
            <strong>{t('signoffs.reviewNotes')}:</strong> {row.review_notes}
          </p>
        )}
        {row.reviewed_at && (
          <p className="ms-card__reviewed">
            {t('signoffs.reviewedBy', {
              name:
                row.reviewed_by_display_name ??
                row.reviewed_by_oid?.slice(0, 8) + '…',
              when: formatDateTime(row.reviewed_at),
            })}
          </p>
        )}
      </div>

      {row.status === 'pending' && (
        <div className="ms-card__actions">
          {!isReturning ? (
            <>
              <button
                type="button"
                className="ms-button ms-button--primary"
                disabled={busy}
                onClick={onApprove}
              >
                {busy ? t('signoffs.busy') : t('signoffs.approve')}
              </button>
              <button
                type="button"
                className="ms-button ms-button--danger"
                disabled={busy}
                onClick={() => onSetReturning(true)}
              >
                {t('signoffs.return')}
              </button>
            </>
          ) : (
            <div className="ms-return">
              <label className="ms-return__label" htmlFor={`ret-${row.signoff_id}`}>
                {t('signoffs.returnNotesLabel')}
              </label>
              <textarea
                id={`ret-${row.signoff_id}`}
                className="ms-return__textarea"
                rows={3}
                value={returnNotes}
                onChange={(e) => onReturnNotesChange(e.target.value)}
                autoFocus
              />
              <div className="ms-return__actions">
                <button
                  type="button"
                  className="ms-button"
                  onClick={() => onSetReturning(false)}
                >
                  {t('signoffs.cancel')}
                </button>
                <button
                  type="button"
                  className="ms-button ms-button--danger"
                  disabled={busy || returnNotes.trim().length === 0}
                  onClick={onSubmitReturn}
                >
                  {busy ? t('signoffs.busy') : t('signoffs.confirmReturn')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </li>
  );
}

// ============================================================================
// Session-review modal — shows the cycle's recorded factors so the manager
// can see what changed before approving / returning.
// ============================================================================

function SessionReviewModal({
  row,
  busy,
  onClose,
  onApprove,
  onReturn,
}: {
  row: SignoffRow;
  busy: boolean;
  onClose: () => void;
  onApprove: () => Promise<void>;
  onReturn: (notes: string) => Promise<void>;
}) {
  const { t, i18n } = useTranslation();
  const callApi = useApi();
  const { domains, findSubdomain } = useFramework();
  const [detail, setDetail] = useState<SessionDetailResponse | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [view, setView] = useState<'review' | 'return'>('review');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    let cancelled = false;
    if (!row.assessment_session_id) {
      setLoadErr(t('signoffs.noSession'));
      return;
    }
    void (async () => {
      try {
        const data = await callApi<SessionDetailResponse>(
          `/whanau/${String(row.whanau_id)}/assessment-sessions/${String(
            row.assessment_session_id,
          )}`,
        );
        if (!cancelled) setDetail(data);
      } catch (err) {
        if (!cancelled) {
          setLoadErr(
            err instanceof ApiError
              ? typeof (err.body as { error?: unknown })?.error === 'string'
                ? (err.body as { error: string }).error
                : err.message
              : err instanceof Error
                ? err.message
                : 'Failed to load session',
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Group assessments by domain for display.
  const grouped = new Map<string, SessionAssessmentRow[]>();
  if (detail) {
    for (const d of domains) grouped.set(d.id, []);
    for (const a of detail.assessments) {
      const list = grouped.get(a.domain_id);
      if (list) list.push(a);
    }
  }

  return createPortal(
    <div
      className="ms-modal__backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="ms-modal" role="dialog" aria-modal="true">
        <header className="ms-modal__header">
          <h2 className="ms-modal__title">
            {row.whanau_display_name ?? `#${String(row.whanau_id)}`}
            {row.cycle_label && (
              <span className="ms-modal__cycle">— {row.cycle_label}</span>
            )}
          </h2>
          <button
            type="button"
            className="ms-modal__close"
            onClick={onClose}
            aria-label={t('signoffs.cancel')}
          >
            ×
          </button>
        </header>

        <div className="ms-modal__body">
          <p className="ms-modal__meta">
            {t('signoffs.requestedBy', {
              name:
                row.requested_by_display_name ??
                row.requested_by_oid.slice(0, 8) + '…',
            })}
            {' · '}
            {formatDateTime(row.requested_at)}
            {row.session_setting && (
              <>
                {' · '}
                <em>{row.session_setting}</em>
              </>
            )}
          </p>

          {loadErr && <p className="ms__error">{loadErr}</p>}
          {!detail && !loadErr && (
            <p className="ms-modal__status">{t('signoffs.loadingSession')}</p>
          )}
          {detail && (
            <>
              {detail.session.session_notes && (
                <section className="ms-review__section">
                  <h3 className="ms-review__heading">
                    {t('signoffs.sessionNotes')}
                  </h3>
                  <p className="ms-review__notes">
                    {detail.session.session_notes}
                  </p>
                </section>
              )}
              {[...grouped.entries()].map(([domainId, rows]) => {
                if (rows.length === 0) return null;
                const domain = domains.find((d) => d.id === domainId);
                if (!domain) return null;
                return (
                  <section key={domainId} className="ms-review__section">
                    <h3 className="ms-review__heading">
                      {frameworkLabel(domain, 'domain', i18n.language, t)}
                    </h3>
                    <ul className="ms-review__rows">
                      {rows.map((a) => {
                        const sub = findSubdomain(a.subdomain_id);
                        const label = sub
                          ? frameworkLabel(sub, 'subdomain', i18n.language, t)
                          : a.subdomain_id;
                        return (
                          <li key={a.subdomain_id} className="ms-review__row">
                            <span className="ms-review__name">{label}</span>
                            <span
                              className={`ms-review__state ms-review__state--${a.state_kind}`}
                            >
                              <StateIcon
                                state={a.state_kind}
                                className="ms-review__icon"
                              />
                              {t(
                                `framework.state.${
                                  a.state_kind === 'not-assessed'
                                    ? 'notAssessed'
                                    : a.state_kind
                                }.descriptor`,
                              )}
                            </span>
                            {(a.kaimahi_notes || a.shared_notes) && (
                              <span className="ms-review__hasnotes">●</span>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </section>
                );
              })}
            </>
          )}
        </div>

        <footer className="ms-modal__footer">
          {view === 'review' ? (
            <>
              <button
                type="button"
                className="ms-button ms-button--danger"
                disabled={busy}
                onClick={() => setView('return')}
              >
                {t('signoffs.return')}
              </button>
              <button
                type="button"
                className="ms-button"
                onClick={onClose}
              >
                {t('signoffs.cancel')}
              </button>
              <button
                type="button"
                className="ms-button ms-button--primary"
                disabled={busy || !!loadErr}
                onClick={() => void onApprove()}
              >
                {busy ? t('signoffs.busy') : t('signoffs.approve')}
              </button>
            </>
          ) : (
            <div className="ms-modal__return-form">
              <label htmlFor="modal-return-notes" className="ms-return__label">
                {t('signoffs.returnNotesLabel')}
              </label>
              <textarea
                id="modal-return-notes"
                rows={3}
                className="ms-return__textarea"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                autoFocus
              />
              <div className="ms-return__actions">
                <button
                  type="button"
                  className="ms-button"
                  onClick={() => {
                    setView('review');
                    setNotes('');
                  }}
                >
                  {t('signoffs.cancel')}
                </button>
                <button
                  type="button"
                  className="ms-button ms-button--danger"
                  disabled={busy || notes.trim().length === 0}
                  onClick={() => void onReturn(notes.trim())}
                >
                  {busy ? t('signoffs.busy') : t('signoffs.confirmReturn')}
                </button>
              </div>
            </div>
          )}
        </footer>
      </div>
    </div>,
    document.body,
  );
}
